import clsx from 'clsx';
import { Clock, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import type { Lead } from '../../api/client';

export type SLAStatus = 'ok' | 'warning' | 'escalated';

interface SLAStatusIndicatorProps {
  lead: Lead;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export function getSLAStatus(lead: Lead): SLAStatus {
  if (lead.sla_escalated_at) return 'escalated';
  if (lead.sla_warning_sent_at) return 'warning';
  return 'ok';
}

export function SLAStatusIndicator({
  lead,
  size = 'md',
  showLabel = false,
  className,
}: SLAStatusIndicatorProps) {
  const status = getSLAStatus(lead);

  // Don't show indicator for unassigned leads or closed leads
  if (!lead.assigned_to || lead.status === 'closed') {
    return null;
  }

  const config = {
    ok: {
      icon: CheckCircle,
      bg: 'bg-green-100',
      text: 'text-green-700',
      label: 'On Track',
    },
    warning: {
      icon: AlertTriangle,
      bg: 'bg-yellow-100',
      text: 'text-yellow-700',
      label: 'Warning',
    },
    escalated: {
      icon: AlertCircle,
      bg: 'bg-red-100',
      text: 'text-red-700',
      label: 'Escalated',
    },
  };

  const { icon: Icon, bg, text, label } = config[status];

  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  if (status === 'ok' && !showLabel) {
    return null; // Don't show anything if OK and no label requested
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full font-medium',
        bg,
        text,
        {
          'px-1.5 py-0.5 text-xs gap-1': size === 'sm',
          'px-2 py-0.5 text-sm gap-1.5': size === 'md',
          'px-2.5 py-1 text-base gap-2': size === 'lg',
        },
        className
      )}
      title={`SLA Status: ${label}`}
    >
      <Icon className={iconSize[size]} />
      {showLabel && label}
    </span>
  );
}

interface SLATimeIndicatorProps {
  lead: Lead;
  className?: string;
}

export function SLATimeIndicator({ lead, className }: SLATimeIndicatorProps) {
  if (!lead.assigned_to || lead.status === 'closed' || !lead.status_changed_at) {
    return null;
  }

  const statusChanged = new Date(lead.status_changed_at);
  const now = new Date();
  const hoursInStatus = Math.floor((now.getTime() - statusChanged.getTime()) / (1000 * 60 * 60));
  const daysInStatus = Math.floor(hoursInStatus / 24);

  let timeLabel: string;
  if (daysInStatus >= 1) {
    timeLabel = `${daysInStatus}d ${hoursInStatus % 24}h`;
  } else {
    timeLabel = `${hoursInStatus}h`;
  }

  const status = getSLAStatus(lead);

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 text-xs',
        {
          'text-gray-500': status === 'ok',
          'text-yellow-600': status === 'warning',
          'text-red-600': status === 'escalated',
        },
        className
      )}
      title={`In "${lead.status}" status for ${hoursInStatus} hours`}
    >
      <Clock className="w-3 h-3" />
      {timeLabel}
    </span>
  );
}

interface SLASummaryCardProps {
  summary: {
    total_assigned: number;
    ok_count: number;
    warning_count: number;
    escalated_count: number;
  };
  className?: string;
}

export function SLASummaryCard({ summary, className }: SLASummaryCardProps) {
  const items = [
    { label: 'On Track', count: summary.ok_count, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Warning', count: summary.warning_count, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    { label: 'Escalated', count: summary.escalated_count, color: 'text-red-600', bg: 'bg-red-50' },
  ];

  return (
    <div className={clsx('grid grid-cols-3 gap-4', className)}>
      {items.map((item) => (
        <div
          key={item.label}
          className={clsx('rounded-lg p-4 text-center', item.bg)}
        >
          <div className={clsx('text-2xl font-bold', item.color)}>{item.count}</div>
          <div className="text-sm text-gray-600">{item.label}</div>
        </div>
      ))}
    </div>
  );
}
